import { LocalStorageKeys, SessionStorageKeys } from "../models/StorageKeys";
import { Logger } from "./Logger";

/* istanbul ignore file */
export class StorageWrapper {
    constructor(private logger: Logger) {}

    private getRoamingSettings(): Office.RoamingSettings | null {
        try {
            return Office?.context?.roamingSettings ?? null;
        } catch {
            return null;
        }
    }

    private getAddinScopedKey(key: string): string {
        try {
            // __MANIFEST_ID__ is provided by webpack DefinePlugin.
            const manifestId = typeof __MANIFEST_ID__ !== "undefined" ? __MANIFEST_ID__ : "";
            if (manifestId && String(manifestId).trim() !== "") {
                return `${manifestId}:${key}`;
            }
        } catch {
            // ignore
        }
        return key;
    }

    /**
     * Determines whether roamingSettings should be used for auth storage.
     *
     * - If the localStorage key "useRoaming" is "true", roaming is forced on all platforms.
     * - Otherwise, roaming is used only on mobile (iOS / Android) and classic desktop (PC / Mac).
     * - Web (OfficeOnline) and Universal (new Outlook) default to localStorage.
     */
    shouldUseRoaming(): boolean {
        try {
            if (localStorage.getItem("useRoaming") === "true") {
                return true;
            }
        } catch {
            // ignore – localStorage may be unavailable
        }

        try {
            const platform = Office?.context?.platform ?? null;
            if (platform === null) {
                // Classic Outlook on Windows often reports null for platform
                return true;
            }
            switch (platform) {
                case Office.PlatformType.PC:
                case Office.PlatformType.Mac:
                case Office.PlatformType.iOS:
                case Office.PlatformType.Android:
                    return true;
                case Office.PlatformType.OfficeOnline:
                case Office.PlatformType.Universal:
                default:
                    return false;
            }
        } catch {
            // Office.js not available – fall back to localStorage
            return false;
        }
    }

    /**
     * Loads an auth value from localStorage.
     */
    loadAuthStorage(key: LocalStorageKeys): string | null {
        return this.loadLocalStorage(key);
    }

    /**
     * Saves an auth value to localStorage.
     */
    saveAuthStorage(key: LocalStorageKeys, value: string): void {
        this.saveLocalStorage(key, value);
    }

    removeAuthStorageItem(key: LocalStorageKeys): void {
        this.removeLocalStorageItem(key);
    }

    /**
     * @deprecated No longer migrates to roaming. Auth tokens are stored in localStorage only.
     * Kept as a no-op so callers don't need to be updated immediately.
     */
    async migrateAuthStorageToRoaming(): Promise<void> {
        // No-op: auth storage now uses localStorage exclusively.
    }

    saveSessionStorage(key: SessionStorageKeys, value: string): void {
        try {
            sessionStorage.setItem(key, value);
        } catch (error) {
            this.logger.error(`StorageWrapper. Failed to save sessionStorage. key: ${key}, value: ${value}`, error);
        }
    }

    loadSessionStorage(key: SessionStorageKeys): string | null {
        try {
            return sessionStorage.getItem(key);
        } catch (error) {
            this.logger.error(`StorageWrapper. Failed to retrieve sessionStorage. key: ${key}`, error);
            return null;
        }
    }

    saveLocalStorage(key: LocalStorageKeys, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (error) {
            this.logger.error(`StorageWrapper. Failed to save localStorage. key: ${key}, value: ${value}`, error);
        }
    }

    loadLocalStorage(key: LocalStorageKeys): string | null {
        try {
            return localStorage.getItem(key);
        } catch (error) {
            this.logger.error(`StorageWrapper. Failed to retrieve localStorage. key: ${key}`, error);
            return null;
        }
    }

    removeLocalStorageItem(key: LocalStorageKeys): void {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            this.logger.error(`StorageWrapper. Failed to remove localStorage key: ${key}`, error);
        }
    }
}
